import { useEffect, useState } from "react";
import { Clock, Users, LogIn, LogOut, Pencil, Trash2, Plus } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { norm } from "@/lib/format";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const hoy = () => new Date().toISOString().slice(0, 10);
const hora = (iso) => (iso
  ? new Date(iso).toLocaleTimeString("es-DO", { hour: "2-digit", minute: "2-digit" })
  : "—");
const horaInput = (iso) => (iso
  ? new Date(iso).toLocaleTimeString("es-DO", { hour12: false, hour: "2-digit", minute: "2-digit" })
  : "");
const dia = (f) => new Date(`${f}T12:00:00`).toLocaleDateString("es-DO",
  { weekday: "short", day: "2-digit", month: "short" });

const Kiosco = ({ empleados, onListo }) => {
  const [busqueda, setBusqueda] = useState("");
  const [eid, setEid] = useState("");
  const [enviando, setEnviando] = useState(false);
  const q = norm(busqueda.trim());
  const opciones = empleados.filter((e) => !q
    || [e.nombre, e.cedula, e.cargo].filter(Boolean).some((v) => norm(v).includes(q)));
  const elegido = empleados.find((e) => e.id === eid);

  const ponchar = async (tipo) => {
    if (!eid) return toast.error("Elija el empleado que va a ponchar");
    setEnviando(true);
    try {
      const { data } = await api.post("/nomina/ponches", { empleado_id: eid, tipo });
      toast.success(tipo === "entrada"
        ? `Entrada de ${elegido.nombre} a las ${hora(data.ponche.entrada)}`
        : `Salida de ${elegido.nombre} a las ${hora(data.ponche.salida)} · ${data.horas} hora(s)`);
      onListo();
    } catch (e) {
      toast.error(apiError(e));
    }
    setEnviando(false);
  };

  return (
    <div className="mb-8 rounded-lg border border-primary/30 bg-primary/5 p-6" data-testid="kiosco-ponche">
      <h3 className="font-display text-base font-semibold">Ponchador de recepción</h3>
      <p className="mb-4 text-sm text-muted-foreground">
        Marque la entrada o la salida de cualquier empleado de la nómina que no poncha desde su
        portal (conserjería, cocina, mantenimiento…). La hora la registra el sistema.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Buscar empleado</Label>
          <Input value={busqueda} placeholder="Nombre, cédula o cargo…" data-testid="kiosco-buscar"
                 onChange={(e) => setBusqueda(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Empleado</Label>
          <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={eid} data-testid="kiosco-empleado" onChange={(e) => setEid(e.target.value)}>
            <option value="">Seleccione…</option>
            {opciones.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre}{e.cargo ? ` — ${e.cargo}` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <Button className="flex-1 rounded-full py-6" disabled={enviando}
                onClick={() => ponchar("entrada")} data-testid="kiosco-entrada">
          <LogIn className="mr-2 h-4 w-4" /> Ponchar entrada
        </Button>
        <Button variant="outline" className="flex-1 rounded-full py-6 bg-card" disabled={enviando}
                onClick={() => ponchar("salida")} data-testid="kiosco-salida">
          <LogOut className="mr-2 h-4 w-4" /> Ponchar salida
        </Button>
      </div>
    </div>
  );
};

const marcaVacia = (empleados) => ({
  empleado_id: empleados[0]?.id || "", fecha: hoy(), entrada: "08:00", salida: "16:00", nota: "",
});

export const PonchesPanel = ({ empleados }) => {
  const [rango, setRango] = useState({ desde: hoy(), hasta: hoy(), empleado_id: "" });
  const [datos, setDatos] = useState(null);
  const [marca, setMarca] = useState(null);
  const [recarga, setRecarga] = useState(0);

  useEffect(() => {
    api.get("/ponches", { params: { desde: rango.desde, hasta: rango.hasta,
                                    empleado_id: rango.empleado_id || undefined } })
      .then((r) => setDatos(r.data)).catch(() => setDatos({ ponches: [], por_empleado: [] }));
  }, [rango, recarga]);

  const cargar = () => setRecarga((n) => n + 1);

  const guardarMarca = async () => {
    if (!marca.empleado_id) return toast.error("Elija el empleado");
    const cuerpo = { empleado_id: marca.empleado_id, fecha: marca.fecha, entrada: marca.entrada,
                     salida: marca.salida || null, nota: marca.nota || null };
    try {
      if (marca.id) await api.put(`/nomina/ponches/${marca.id}`, cuerpo);
      else await api.post("/nomina/ponches/manual", cuerpo);
      toast.success(marca.id ? "Marca corregida" : "Marca registrada a mano");
      setMarca(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const borrar = async (p) => {
    try {
      await api.delete(`/nomina/ponches/${p.id}`);
      toast.success("Marca eliminada");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="panel-ponches">
      <Kiosco empleados={empleados} onListo={cargar} />

      <div className="mb-6 grid gap-4 sm:grid-cols-4">
        <div className="space-y-2">
          <Label>Desde</Label>
          <Input type="date" value={rango.desde} data-testid="ponches-desde"
                 onChange={(e) => setRango({ ...rango, desde: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Hasta</Label>
          <Input type="date" value={rango.hasta} data-testid="ponches-hasta"
                 onChange={(e) => setRango({ ...rango, hasta: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Empleado</Label>
          <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                  value={rango.empleado_id} data-testid="ponches-empleado"
                  onChange={(e) => setRango({ ...rango, empleado_id: e.target.value })}>
            <option value="">Todos</option>
            {empleados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <Button variant="outline" className="w-full rounded-full" data-testid="nueva-marca"
                  onClick={() => setMarca(marcaVacia(empleados))}>
            <Plus className="mr-2 h-4 w-4" /> Marca a mano
          </Button>
        </div>
      </div>

      {datos && (
        <>
          <div className="mb-6 flex flex-wrap gap-3">
            <Badge variant="outline" data-testid="ponches-total">
              <Clock className="mr-1 inline h-3 w-3" /> {datos.horas_total} hora(s) en el rango
            </Badge>
            <Badge variant="outline" data-testid="ponches-empleados">
              <Users className="mr-1 inline h-3 w-3" /> {datos.por_empleado.length} empleado(s)
            </Badge>
            {datos.tardanzas > 0 && (
              <Badge variant="destructive" data-testid="ponches-tardanzas">
                {datos.tardanzas} tardanza(s) en el rango
              </Badge>
            )}
          </div>

          <div className="overflow-hidden rounded-lg border border-border/60 bg-card">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm" data-testid="tabla-ponches">
                <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3">Empleado</th>
                    <th className="px-5 py-3">Día</th>
                    <th className="px-5 py-3">Entrada</th>
                    <th className="px-5 py-3">Salida</th>
                    <th className="px-5 py-3">Horas</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {datos.ponches.map((p) => (
                    <tr key={p.id} className={`border-t border-border/60 ${
                      p.tarde ? "bg-destructive/5" : ""}`}>
                      <td className="px-5 py-3">
                        <span className="block">{p.nombre}</span>
                        {(p.manual || p.registrado_por) && (
                          <span className="block text-xs text-muted-foreground">
                            {p.manual ? "a mano" : "recepción"}
                            {p.registrado_por ? ` · ${p.registrado_por}` : ""}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 capitalize">{dia(p.fecha)}</td>
                      <td className="px-5 py-3 font-mono text-xs">
                        {p.tarde ? (
                          <span className="font-semibold text-destructive"
                                data-testid={`tardanza-${p.id}`}>
                            {hora(p.entrada)} · {p.minutos_tarde} min tarde
                            {p.hora_pactada ? ` (pactada ${p.hora_pactada})` : ""}
                          </span>
                        ) : hora(p.entrada)}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs">
                        {p.salida ? hora(p.salida) : <Badge variant="outline">sin salida</Badge>}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs">{p.horas ? `${p.horas} h` : "—"}</td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button size="sm" variant="outline" className="rounded-full"
                                  data-testid={`corregir-ponche-${p.id}`}
                                  onClick={() => setMarca({
                                    id: p.id, empleado_id: p.empleado_id, fecha: p.fecha,
                                    entrada: horaInput(p.entrada), salida: horaInput(p.salida),
                                    nota: p.nota || "",
                                  })}>
                            <Pencil className="mr-1 h-3 w-3" /> Corregir
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => borrar(p)}
                                  data-testid={`borrar-ponche-${p.id}`}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {datos.ponches.length === 0 && (
                <p className="p-10 text-center text-sm text-muted-foreground" data-testid="ponches-admin-vacios">
                  No hay ponches en el rango seleccionado.
                </p>
              )}
            </div>
          </div>

          {datos.por_empleado.length > 0 && (
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="resumen-ponches">
              {datos.por_empleado.map((e) => (
                <div key={e.empleado_id} className="rounded-md border border-border/60 bg-card p-4">
                  <p className="text-sm font-medium">{e.nombre}</p>
                  <p className="text-xs text-muted-foreground">
                    {e.marcas} marca(s) · {e.horas} hora(s)
                  </p>
                  {e.tardanzas > 0 && (
                    <p className="text-xs font-semibold text-destructive"
                       data-testid={`tardanzas-empleado-${e.empleado_id}`}>
                      {e.tardanzas} tardanza(s) · {e.minutos_tarde} min acumulados
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
          {datos.ponches.length === 0 && datos.por_empleado.length === 0 && (
            <div className="mt-6"><Empty testId="sin-ponches">Nadie ha ponchado en ese rango.</Empty></div>
          )}
        </>
      )}

      <Dialog open={!!marca} onOpenChange={(o) => !o && setMarca(null)}>
        <DialogContent data-testid="modal-marca">
          <DialogHeader>
            <DialogTitle className="font-display">
              {marca?.id ? "Corregir marca" : "Registrar marca a mano"}
            </DialogTitle>
            <DialogDescription>
              Para olvidos de ponche. Queda registrado que la marca se hizo a mano y quién la hizo.
            </DialogDescription>
          </DialogHeader>
          {marca && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Empleado</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={marca.empleado_id} data-testid="marca-empleado" disabled={!!marca.id}
                        onChange={(e) => setMarca({ ...marca, empleado_id: e.target.value })}>
                  {empleados.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Input type="date" value={marca.fecha} data-testid="marca-fecha"
                       onChange={(e) => setMarca({ ...marca, fecha: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Entrada</Label>
                <Input type="time" value={marca.entrada} data-testid="marca-entrada"
                       onChange={(e) => setMarca({ ...marca, entrada: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Salida (opcional)</Label>
                <Input type="time" value={marca.salida} data-testid="marca-salida"
                       onChange={(e) => setMarca({ ...marca, salida: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Motivo o nota</Label>
                <Input value={marca.nota} placeholder="Olvidó ponchar la salida" data-testid="marca-nota"
                       onChange={(e) => setMarca({ ...marca, nota: e.target.value })} />
              </div>
              <Button className="rounded-full py-6 sm:col-span-2" onClick={guardarMarca}
                      data-testid="guardar-marca">
                {marca.id ? "Guardar corrección" : "Registrar marca"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
